import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import { Card } from '@workspace/ui/components/card';
import { toast } from '@workspace/ui/components/sonner';
import { Spinner } from '@workspace/ui/components/spinner';
import { ArrowRight, CircleCheckBig } from 'lucide-react';
import { useEffect } from 'react';
import { useAuth } from '~/auth/use-auth';
import { m } from '~/paraglide/messages.js';
import { setLocalStorageItem } from '~/utils/local-storage';
import { acceptInvitation, getInviteByCode } from './-api/invite';
import { InvitationErrorPage } from './-components/invitation-error-page';

type InviteSearchParams = {
  code: string;
  roles: 'admin' | 'employee';
};

export const Route = createFileRoute('/_shared/invite/')({
  validateSearch: (search: Record<string, unknown>): InviteSearchParams => ({
    code: (search.code as string) || '',
    roles: (search.roles as InviteSearchParams['roles']) || 'employee',
  }),
  beforeLoad: ({ search }) => {
    if (search.roles === 'employee') {
      window.location.href = `https://cuenta.bloque.app/#/invite-link/?code=${search.code}`;
      return;
    }
  },
  component: InvitePage,
});

function InvitePage() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const {
    data: invite,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['invite', code],
    queryFn: () => getInviteByCode(code),
    enabled: !!code,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptInvitation(code),
    onSuccess: () => {
      navigate({ to: '/' });
    },
    onError: (error) => {
      console.error('Error accepting invitation:', error);
    },
  });

  const handleAcceptInvitation = async () => {
    if (isAuthenticated) {
      await acceptMutation.mutateAsync();
      toast.success(m['invite.page.toast_accepted']());
      return;
    }

    toast.warning(m['invite.page.toast_must_authenticate']());
    setLocalStorageItem('invite', window.location.href);
    setTimeout(() => {
      // @ts-expect-error
      navigate({ to: '/auth/login' });
    }, 2000);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate function is stable and doesn't need to be in dependencies
  useEffect(() => {
    if (isAuthenticated && invite?.status === 'accepted') {
      toast.success(m['invite.page.toast_already_accepted_continue']());
      navigate({ to: '/' });
      return;
    }

    if (invite?.status === 'accepted') {
      toast.error(m['invite.page.toast_already_accepted_login']());
      // @ts-expect-error
      navigate({ to: '/auth/login' });
    }
  }, [isAuthenticated, invite]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!code || error || !invite) {
    return <InvitationErrorPage />;
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header with checkmark icon */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <CircleCheckBig className="w-8 h-8 text-white" />
          </div>

          <h1 className="text-white text-2xl font-bold mb-4">
            {m['invite.page.title']({
              name: invite.details.membership_info.name,
              orgName: invite.org_info.name,
            })}
          </h1>

          <p className="text-muted-foreground text-sm font-medium">
            {m['invite.page.subtitle']({
              orgName: invite.org_info.name,
            })}
          </p>
        </div>

        {/* Invitation details card */}
        <Card className="bg-muted p-6 mb-6 gap-4">
          <p className="text-muted-foreground text-sm font-medium mb-0">
            {m['invite.page.review_details']()}
          </p>

          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.invited_as']()}
              </p>
              <p className="text-white font-medium">
                {invite.details.membership_info.name} (
                {invite.details.membership_info.email})
              </p>
            </div>

            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.employer']()}
              </p>
              <p className="text-white font-medium">{invite.org_info.name}</p>
            </div>

            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.position']()}
              </p>
              <p className="text-white font-medium">
                {invite.details.membership_info.title ||
                  m['invite.page.not_specified']()}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.contract_type']()}
              </p>
              <p className="text-white font-medium">
                {m['invite.page.contract_type_not_provided']()}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.compensation']()}
              </p>
              <div className="mb-1">
                <span className="text-4xl font-semibold">$0</span>
                <span className="text-xl align-top">.00</span>
                <span className="text-4xl font-semibold"> USD</span>
              </div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.monthly']()}
              </p>
            </div>

            <div>
              <p className="text-muted-foreground text-sm font-medium">
                {m['invite.page.contract_duration']()}
              </p>
              <p className="text-white font-medium">
                {m['invite.page.not_specified']()}
              </p>
            </div>
          </div>
        </Card>

        {/* Accept invitation button */}
        <Button
          className="w-full bg-white text-black hover:bg-gray-100 font-medium py-3 mb-6"
          size="lg"
          onClick={handleAcceptInvitation}
          disabled={acceptMutation.isPending}
        >
          {acceptMutation.isPending ? (
            <>
              <div className="w-4 h-4 mr-2">
                <Spinner />
              </div>
              {m['invite.page.accepting']()}
            </>
          ) : (
            <>
              {m['invite.page.accept_invitation']()}
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        {/* Footer text */}
        <p className="text-muted-foreground text-sm text-center leading-relaxed">
          {m['invite.page.footer_text']()}{' '}
          <a
            href="https://drive.google.com/file/u/3/d/1hdfdncq6RLkLThwTltISRPBJlwQxlpH6/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline cursor-pointer hover:text-gray-300"
          >
            {m['invite.page.terms_of_service']()}
          </a>{' '}
          {m['invite.page.and']()}{' '}
          <a
            href="https://drive.google.com/file/u/3/d/1Zwn9PtdecCH_VVvNctnCa0oO65Com7QQ/view"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline cursor-pointer hover:text-gray-300"
          >
            {m['invite.page.privacy_policy']()}
          </a>
        </p>
      </div>
    </div>
  );
}
